import { ChevronLeft, ChevronRight, History, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReviewTable } from '../components/ReviewTable';
import { Button, buttonClasses } from '../components/ui/Button';
import { EmptyState, ErrorState, PageHeader, SkeletonRows } from '../components/ui/Feedback';
import { useReviews } from '../hooks/queries';
import type { ReviewListParams, ReviewStatus } from '../types/api';

const SORTS: Array<{ value: string; label: string; sort: ReviewListParams['sort']; order: ReviewListParams['order'] }> = [
  { value: 'newest', label: 'Newest first', sort: 'createdAt', order: 'desc' },
  { value: 'oldest', label: 'Oldest first', sort: 'createdAt', order: 'asc' },
  { value: 'most', label: 'Most findings', sort: 'findingTotal', order: 'desc' },
  { value: 'repo', label: 'Repository A–Z', sort: 'repo', order: 'asc' },
];

function useDebounced<T>(value: T, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function HistoryPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ReviewStatus | ''>('');
  const [sortKey, setSortKey] = useState('newest');
  const [page, setPage] = useState(1);
  const debounced = useDebounced(search.trim());
  const sort = SORTS.find((s) => s.value === sortKey) ?? SORTS[0]!;

  useEffect(() => setPage(1), [debounced, status, sortKey]);

  const params: ReviewListParams = {
    page,
    pageSize: 15,
    search: debounced || undefined,
    status: status || undefined,
    sort: sort.sort,
    order: sort.order,
  };
  const { data, isLoading, isFetching, error, refetch } = useReviews(params);
  const filtered = !!(debounced || status);

  return (
    <div>
      <PageHeader title="Review History" color="pink" description="All reviews in this workspace." />

      <div className="mb-4 grid gap-2 sm:grid-cols-[1fr_auto_auto]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" aria-hidden />
          <label htmlFor="history-search" className="sr-only">
            Search reviews
          </label>
          <input
            id="history-search"
            type="search"
            className="input pl-9"
            placeholder="Search by repository, title or PR number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label htmlFor="history-status" className="sr-only">
          Status
        </label>
        <select id="history-status" className="input" value={status} onChange={(e) => setStatus(e.target.value as ReviewStatus | '')}>
          <option value="">All statuses</option>
          <option value="queued">Queued</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <label htmlFor="history-sort" className="sr-only">
          Sort
        </label>
        <select id="history-sort" className="input" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div className="card" aria-busy={isFetching}>
        {isLoading ? (
          <SkeletonRows rows={6} />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<History className="h-6 w-6" />}
            title={filtered ? 'No reviews match your filters' : 'No reviews yet'}
            description={filtered ? 'Try a different search or status.' : 'Reviews you run will be listed here.'}
            action={
              !filtered && (
                <Link to="/app/new" className={buttonClasses('primary')}>
                  Start a review
                </Link>
              )
            }
          />
        ) : (
          <>
            <ReviewTable reviews={data.items} />
            <nav aria-label="Pagination" className="flex items-center justify-between border-t border-line px-4 py-3 text-sm">
              <span className="text-muted">
                Page {data.page} of {data.totalPages} · {data.total} review{data.total === 1 ? '' : 's'}
              </span>
              <div className="flex gap-2">
                <Button size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} icon={<ChevronLeft className="h-4 w-4" />}>
                  Previous
                </Button>
                <Button size="sm" disabled={page >= data.totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </nav>
          </>
        )}
      </div>
    </div>
  );
}
